import { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link2, Loader2, Plus, UploadCloud } from "lucide-react";
import { ALL_ACCEPTED_TYPES } from "@/features/file-upload";
import { libraryGlowButtonClass } from "@/features/library/lib/library-ui";
import { cn } from "@/lib/utils";
import { usePickerUpload } from "../hooks/use-picker-upload";
import { getAcceptedKinds } from "../lib/pick-compatibility";
import { useFilePickerStore } from "../store/file-picker-store";
import { PickerUploadTile } from "./PickerUploadTile";

const KIND_LABEL_KEYS = {
  image: "filePicker.categories.images",
  video: "filePicker.categories.videos",
  audio: "filePicker.categories.audio",
  document: "filePicker.categories.documents",
} as const;

const isMac = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform);

/** "Yükle" kaynağı: bırakma alanı, URL ile ekleme ve yükleme kuyruğu. */
export const PickerUploadPanel = () => {
  const { t } = useTranslation();
  const config = useFilePickerStore((state) => state.config);
  const uploads = useFilePickerStore((state) => state.uploads);
  const { uploadFiles, uploadUrl } = usePickerUpload();
  const inputRef = useRef<HTMLInputElement>(null);
  const [url, setUrl] = useState("");
  const [isAddingUrl, setIsAddingUrl] = useState(false);

  const maxSelection = config?.maxSelection || 10;
  const acceptedKinds = useMemo(() => getAcceptedKinds(config), [config]);

  const handleAddUrl = async () => {
    if (!url.trim() || isAddingUrl) return;
    setIsAddingUrl(true);
    const added = await uploadUrl(url);
    setIsAddingUrl(false);
    if (added) setUrl("");
  };

  return (
    <div className="h-full min-h-0 overflow-y-auto overscroll-y-contain px-3 sm:px-5 py-4">
      <div className="mx-auto flex max-w-3xl flex-col gap-4">
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className="group relative flex min-h-[240px] sm:min-h-[300px] flex-col items-center justify-center gap-4 overflow-hidden rounded-3xl border-2 border-dashed border-black/10 dark:border-white/15 bg-black/[0.02] dark:bg-white/[0.03] px-6 py-10 text-center transition-colors hover:border-violet-400/70 dark:hover:border-violet-400/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500/60"
        >
          <div
            aria-hidden
            className="pointer-events-none absolute inset-x-0 bottom-0 h-1/2 bg-[radial-gradient(ellipse_60%_80%_at_50%_100%,rgba(139,92,246,0.18),transparent)] opacity-0 transition-opacity duration-300 group-hover:opacity-100"
          />
          <div className="relative w-16 h-16 rounded-2xl bg-gradient-to-br from-indigo-500 via-violet-500 to-fuchsia-500 flex items-center justify-center text-white shadow-lg shadow-violet-500/30 transition-transform duration-300 group-hover:scale-105">
            <UploadCloud className="w-7 h-7" />
          </div>
          <div className="relative flex flex-col gap-1.5">
            <span className="text-lg sm:text-xl font-semibold text-text-primary dark:text-white">
              {t("filePicker.uploadFiles")}
            </span>
            <span className="text-sm text-text-tertiary dark:text-gray-400">
              {t("filePicker.dragDropOrClick")}
            </span>
            <span className="text-xs text-text-quaternary dark:text-gray-500">
              {t("filePicker.pasteHint", { shortcut: isMac ? "⌘V" : "Ctrl+V" })}
            </span>
          </div>
          {acceptedKinds && acceptedKinds.size > 0 && (
            <div className="relative flex flex-wrap justify-center gap-1.5">
              {[...acceptedKinds].map((kind) => (
                <span
                  key={kind}
                  className="rounded-full bg-black/[0.05] dark:bg-white/[0.08] px-2.5 py-1 text-xs font-medium text-text-secondary dark:text-gray-300"
                >
                  {t(KIND_LABEL_KEYS[kind])}
                </span>
              ))}
            </div>
          )}
        </button>

        <div className="flex items-center gap-2">
          <div className="relative flex-1 min-w-0">
            <Link2 className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-quaternary dark:text-gray-500" />
            <input
              type="url"
              inputMode="url"
              value={url}
              onChange={(event) => setUrl(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  void handleAddUrl();
                }
              }}
              placeholder={t("filePicker.enterUrl")}
              className="h-11 w-full rounded-xl bg-black/[0.04] dark:bg-white/[0.06] pl-9 pr-3 text-sm text-text-primary dark:text-white placeholder:text-text-quaternary dark:placeholder:text-gray-500 ring-1 ring-inset ring-transparent focus:outline-none focus:ring-violet-500/50"
            />
          </div>
          <button
            type="button"
            onClick={() => void handleAddUrl()}
            disabled={!url.trim() || isAddingUrl}
            className={cn(
              libraryGlowButtonClass,
              "h-11 shrink-0 px-4 rounded-xl inline-flex items-center gap-1.5 text-sm font-medium bg-black/[0.04] dark:bg-white/[0.06] ring-1 ring-inset ring-black/[0.06] dark:ring-white/[0.08] text-text-secondary dark:text-gray-200 hover:text-text-primary dark:hover:text-white disabled:opacity-40 disabled:pointer-events-none"
            )}
          >
            {isAddingUrl ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            {t("filePicker.add")}
          </button>
        </div>

        {uploads.length > 0 && (
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 sm:gap-3">
            {uploads.map((upload) => (
              <PickerUploadTile key={upload.id} upload={upload} />
            ))}
          </div>
        )}
      </div>

      <input
        ref={inputRef}
        type="file"
        multiple={maxSelection > 1}
        className="hidden"
        accept={config?.allowedTypes?.join(",") || ALL_ACCEPTED_TYPES.join(",")}
        onChange={(event) => {
          if (event.target.files?.length) void uploadFiles(event.target.files);
          event.target.value = "";
        }}
      />
    </div>
  );
};
